import React, { useRef, useState } from 'react';
import { ImagePlus, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { uploadApi } from '../../services/api';

const ImageUpload = ({
  value,
  onChange,
  label = 'Şəkil yüklə',
  multiple = false,
  className = '',
}) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const images = multiple
    ? Array.isArray(value)
      ? value
      : []
    : value
    ? [value]
    : [];

  const handleClick = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);

    if (!files.length) return;

    try {
      setUploading(true);

      if (multiple) {
        if (files.length > 6) {
          toast.error('Ən çox 6 şəkil seçə bilərsiniz');
          return;
        }

        const res = await uploadApi.uploadBagMultiple(files);
        const uploadedPaths = res.data.files.map((file) => file.path);

        onChange(uploadedPaths);
        toast.success('Şəkillər uğurla yükləndi');
      } else {
        const file = files[0];
        const res = await uploadApi.uploadBagSingle(file);
        const uploadedPath = res.data.file.path;

        onChange(uploadedPath);
        toast.success('Şəkil uğurla yükləndi');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Şəkil yükləmək mümkün olmadı');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeImage = (index) => {
    if (multiple) {
      const updated = images.filter((_, i) => i !== index);
      onChange(updated);
    } else {
      onChange('');
    }
  };

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-brand-brown mb-2">
          {label}
        </label>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple={multiple}
        className="hidden"
        onChange={handleFileChange}
      />

      <div
        onClick={handleClick}
        className="border-2 border-dashed border-brand-secondary/40 rounded-2xl p-6 cursor-pointer hover:border-brand-primary transition-colors bg-brand-muted/5"
      >
        <div className="flex flex-col items-center justify-center text-center">
          {uploading ? (
            <>
              <Loader2 className="w-8 h-8 animate-spin text-brand-primary mb-3" />
              <p className="text-sm text-brand-muted">Yüklənir...</p>
            </>
          ) : (
            <>
              <ImagePlus className="w-8 h-8 text-brand-primary mb-3" />
              <p className="text-sm text-brand-brown font-medium">
                Şəkil seçmək üçün klik et
              </p>
              <p className="text-xs text-brand-muted mt-1">
                PNG, JPG, WEBP
              </p>
            </>
          )}
        </div>
      </div>

      {images.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
          {images.map((img, index) => (
            <div
              key={index}
              className="relative group rounded-xl overflow-hidden bg-brand-muted/10"
              data-testid={`image-preview-${index}`}
            >
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}${img}`}
                alt={`Preview ${index + 1}`}
                className="w-full h-32 object-cover"
              />

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeImage(index);
                }}
                className="absolute top-2 right-2 w-7 h-7 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`remove-image-${index}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageUpload;